<template>
  <div class="app-shell">
    <!-- Skip to main content — WCAG 2.4.1 -->
    <a href="#main-content" class="skip-link">Skip to main content</a>
    <AppNav v-if="auth.isLoggedIn" />
    <main id="main-content" class="app-main">
      <RouterView v-slot="{ Component }">
        <Transition :name="useTransition ? 'page' : ''" mode="out-in">
          <component :is="Component" />
        </Transition>
      </RouterView>
    </main>
    <AppTabBar v-if="auth.isLoggedIn" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { useAuthStore } from './stores/auth.js'
import { requestMediaPermissions } from './services/native.js'
import AppNav from './components/layout/AppNav.vue'
import AppTabBar from './components/layout/AppTabBar.vue'

const auth   = useAuthStore()
const router = useRouter()
const useTransition = !Capacitor.isNativePlatform()

// Request camera + photos permissions once user is logged in
watch(() => auth.isLoggedIn, (loggedIn) => {
  if (loggedIn) requestMediaPermissions()
}, { immediate: true })

// ── Deep link handler (Android / iOS) ─────────────────────────────────────
// Handles two URL schemes:
//   Tykkets://auth/verify?token=xxx  — custom scheme, always opens app (reliable)
//   https://tykkets.com/auth/verify?token=xxx — https scheme (requires domain verification)
onMounted(() => {
  if (!Capacitor.isNativePlatform()) return

  App.addListener('appUrlOpen', async (event) => {
    try {
      const raw = event.url

      // tykkets://localhost/auth/verified — Android post-browser-verify handoff
      // tykkets://localhost/auth/verify?token=xxx — Android direct token
      // Tykkets://auth/verified — iOS post-browser-verify handoff
      // Tykkets://auth/verify?token=xxx — iOS direct token
      const isCustomScheme = raw.startsWith('tykkets://') || raw.startsWith('Tykkets://')
      if (isCustomScheme) {
        if (raw.includes('/auth/verified')) {
          // Browser already verified token and set refresh cookie — just init session
          await auth.init()
          router.replace(auth.isLoggedIn ? '/' : '/login')
          return
        }
        // Extract token from URL
        const normalised = raw
          .replace('tykkets://localhost', 'https://x.com')
          .replace('Tykkets://auth', 'https://x.com/auth')
        const url   = new URL(normalised)
        const token = url.searchParams.get('token')
        if (token) router.push(`/auth/verify?token=${token}`)
        return
      }

      // HTTPS scheme: strip any /uk prefix, navigate inside the app
      const url  = new URL(raw)
      const path = url.pathname.replace(/^\/uk/, '') + url.search
      if (path.startsWith('/auth/verify')) {
        router.push(path)
      }
    } catch {
      // Malformed URL — ignore
    }
  })
})
</script>

<style scoped>
.app-shell {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  padding-top: var(--safe-top);
}
.app-main {
  flex: 1;
  padding-bottom: calc(72px + var(--safe-bottom));
}

.skip-link {
  position: absolute;
  top: -100%;
  left: 16px;
  background: #C8F04A;
  color: #0F1923;
  font-weight: 700;
  font-size: 14px;
  padding: 10px 18px;
  border-radius: 0 0 8px 8px;
  z-index: 9999;
  text-decoration: none;
  transition: top 0.1s;
}
.skip-link:focus { top: 0; }
</style>
